import React, { useEffect, useState } from "react";
import axios from "axios";

import { Container, Row, Col, Form, Button, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { CONTACT_API } from "../../api/api";

import { Helmet } from "react-helmet-async";

import "./ContactUs.css";

const ContactUs = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    course: "",
    role: "",
    // level: "",
    source: "",
  });
  const [showThankYou, setShowThankYou] = useState(false);
  // Loading
  const [isSubmitting, setIsSubmitting] = useState(false);

  // main toast error
  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      if (!/^\d*$/.test(value)) return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();

    // Validation (only required fields)
    const requiredFields = ["firstName", "lastName", "phone", "email"];

    const isEmpty = requiredFields.some(
      (key) => !String(formData[key] || "").trim(),
    );

    if (isEmpty) {
      showErrorToast("Please fill all required fields");
      return;
    }

    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phoneRegex.test(formData.phone)) {
      showErrorToast("Enter a valid 10-digit phone number.");
      return;
    }

    // Gmail validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      showErrorToast("Please enter a valid email address");
      return;
    }

    // API payload
    const apiData = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      phone: formData.phone,
      email: formData.email,
      course: formData.course,
      role: formData.role,
      // learning_level: formData.level,
      hear_about: formData.source,
    };

    setIsSubmitting(true);
    try {
      const response = await axios.post(CONTACT_API.ADD_CONTACT, apiData);

      const data = response.data;

      if (data.statusCode === 200) {
        // toast.success(data.message, {
        //   position: "top-right",
        //   autoClose: 1500,
        //   theme: "colored",
        // });

        setShowThankYou(true);

        setFormData({
          firstName: "",
          lastName: "",
          phone: "",
          email: "",
          course: "",
          role: "",
          // level: "",
          source: "",
        });
      } else {
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Contact API error:", error);

      // Server responded with non-2xx status (like 409)
      if (error.response?.data?.message) {
        showErrorToast(error.response.data.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!showThankYou) return;

    const timer = setTimeout(() => {
      setShowThankYou(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, [showThankYou]);

  return (
    <>
      <Helmet>
        <title>Skilledition | Contact – Courses, Internships & Support</title>

        <meta
          name="description"
          content="Contact Skilledition for course details, internships, mentorship, or support. Our team is here to help you start your learning journey."
        />

        <link rel="canonical" href="https://skilledition.in/contact" />

        {/* Open Graph / Social Share */}
        <meta
          property="og:title"
          content="Skilledition | Contact – Courses, Internships & Support"
        />
        <meta
          property="og:description"
          content="Get in touch with Skilledition for guidance, support, and learning opportunities."
        />
        <meta property="og:url" content="https://skilledition.in/contact" />
        <meta
          property="og:image"
          content="https://skilledition.in/seo-banner-contact.png"
        />
        <meta property="og:type" content="website" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Skilledition | Contact – Courses, Internships & Support"
        />
        <meta
          name="twitter:description"
          content="Get in touch with Skilledition for guidance, support, and learning opportunities."
        />
        <meta
          name="twitter:image"
          content="https://skilledition.in/seo-banner-contact.png"
        />
      </Helmet>

      <section className="skilledition-contactus-section reveal">
        <div className="skilledition-contactus-bg" />

        <Container className="skilledition-contactus-container">
          <div className="skilledition-contactus-header">
            <h2>Contact Us</h2>
            <p>
              Ready to kick-start your career? Join Skilledition's internship
              program and gain hands-on experience with real-world projects. If
              you have any questions about enrollment, courses, internships, or
              certifications, our support team is here to help.
            </p>
          </div>

          <Row>
            {/* Left Info Card */}
            <Col lg={5}>
              <div className="skilledition-contactus-info-card">
                <h5>Talk to the Skilledition Team</h5>
                <p>Our team can help you with:</p>
                <ul>
                  <li>Choosing the right course</li>
                  <li>Real-time internships with live project experience.</li>
                  <li>Building a Professional Resume</li>
                  <li>Course access, progress, and certificates</li>
                  <li>Payment, pricing, and general support</li>
                </ul>
                <p className="mb-0 text-center">
                  Get clear answers before you enroll.
                  <br />
                  no pressure, no confusion.
                </p>
              </div>
            </Col>

            {/* Right Form Card */}
            <Col lg={7}>
              <div className="skilledition-contactus-form-card">
                <Form onSubmit={handleContactSubmit}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>First name*</Form.Label>
                        <Form.Control
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          placeholder="Enter first name"
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Last name*</Form.Label>
                        <Form.Control
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          placeholder="Enter last name"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Phone number*</Form.Label>
                    <Form.Control
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      maxLength={10}
                      inputMode="numeric"
                      placeholder="Enter 10-digit mobile number"
                    />
                  </Form.Group>

                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email address*</Form.Label>
                        <Form.Control
                          type="text"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="Enter email address"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Select your role</Form.Label>
                        <Form.Select
                          name="role"
                          value={formData.role}
                          onChange={handleChange}
                        >
                          <option value="">Select role</option>
                          <option>Student</option>
                          <option>Tutor</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Course you're interested in</Form.Label>
                        <Form.Select
                          name="course"
                          value={formData.course}
                          onChange={handleChange}
                        >
                          <option value="">Select course</option>
                          <option>Full Stack Web Development</option>
                          <option>Frontend Development</option>
                          <option>Backend Development</option>
                          <option>AI Prompt Engineering</option>
                          <option>Cybersecurity</option>
                          <option>Cloud Computing</option>
                          <option>UI/UX Design</option>
                          <option>IT Sales</option>
                          <option>Digital Marketing</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    {/* <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Learning level</Form.Label>
                        <Form.Select
                          name="level"
                          value={formData.level}
                          onChange={handleChange}
                        >
                          <option value="">Select level</option>
                          <option>Beginner</option>
                          <option>Intermediate</option>
                          <option>Advance</option>
                        </Form.Select>
                      </Form.Group>
                    </Col> */}
                  </Row>

                  <Form.Group className="mb-4">
                    <Form.Label>
                      How did you hear about Skilledition?
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="source"
                      value={formData.source}
                      onChange={handleChange}
                    />
                  </Form.Group>

                  <Button
                    type="submit"
                    className="skilledition-contactus-submit-btn"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Submitting..." : "Submit Enquiry"}
                  </Button>
                </Form>
              </div>
            </Col>
          </Row>
        </Container>

        {/* THANK YOU MODAL */}
        <Modal
          show={showThankYou}
          onHide={() => setShowThankYou(false)}
          centered
          dialogClassName="skilledition-contactus-modal"
          backdropClassName="skilledition-contactus-modal-backdrop"
        >
          <Modal.Header className="skilledition-contactus-modal-header">
            <Modal.Title className="skilledition-contactus-modal-title">
              Thank You!
            </Modal.Title>
          </Modal.Header>

          <Modal.Body className="skilledition-contactus-modal-body text-center">
            <h5 className="skilledition-contactus-modal-heading">
              Your enquiry has been submitted successfully
            </h5>
            <p className="skilledition-contactus-modal-text">
              Our Skilledition team will contact you shortly with complete
              course details and guidance.
            </p>
          </Modal.Body>

          <Modal.Footer className="skilledition-contactus-modal-footer">
            <Button
              className="skilledition-contactus-modal-btn"
              onClick={() => setShowThankYou(false)}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </section>
    </>
  );
};

export default ContactUs;
